import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import type { Asset } from 'types/asset.types';
import { SVG } from 'components/Asset/SVG';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { getImageName, mapVehiclesAssets } from 'utils/helpers/mapAssetImage';
import { transformText } from '../FleetOverview/helpers/asset.helpers';
import { PNG } from 'components/Asset/PNG';
import { useTranslation } from 'react-i18next';

const _dark = clientThemePalette[CLIENT_THEME].dark;
const _light = clientThemePalette[CLIENT_THEME].light;

export const Header = ({
  enableDarkTheme,
  assets,
  setShowModal,
  onRemoveAsset,
  onEditAsset,
}: {
  enableDarkTheme: boolean;
  assets: Asset[];
  setShowModal: (show: boolean) => void;
  onRemoveAsset?: (assetId: string) => void;
  onEditAsset?: (assetId: string) => void;
}) => {
  const { t } = useTranslation();
  if (!assets?.length) return null;
  const assetDisplay = [
    {
      bumperNumber: 'Benchmark',
      assetType: assets?.[0].assetType,
    },
    ...assets,
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        mb: 4,
        backgroundColor: 'transparent',
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      <Grid container spacing={2}>
        {assetDisplay.map((item, index) => {
          const pngName = getImageName(item as Asset, 'Small');
          return (
            <Grid item lg={3} md={6} xs={12} key={item.bumperNumber}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: enableDarkTheme
                    ? alpha(_dark.boxBackground, 0.7)
                    : '#fff',
                  borderRadius: '12px',
                  minHeight: '110px',
                  p: 2,
                  textAlign: 'center',
                  color: _dark.textPrimary,
                  border: `1px solid ${
                    enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)'
                  }`,
                  boxShadow: enableDarkTheme
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before':
                    index === 0
                      ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background:
                            'linear-gradient(90deg, #E2C171, #D4AF37)',
                        }
                      : {},
                  '&:hover': {
                    boxShadow: enableDarkTheme
                      ? '0 8px 25px rgba(0, 0, 0, 0.3)'
                      : '0 8px 25px rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                {/* Action buttons for non-benchmark cards */}
                {index > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      display: 'flex',
                      gap: 0.5,
                      opacity: 0.7,
                      transition: 'opacity 0.2s ease',
                      '&:hover': {
                        opacity: 1,
                      },
                    }}
                  >
                    <Tooltip title={t('change_asset')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onEditAsset) {
                            // For Asset type objects, use assetId
                            const assetId =
                              'assetId' in item
                                ? item.assetId
                                : item.bumperNumber;
                            onEditAsset(assetId || '');
                          } else {
                            setShowModal(true);
                          }
                        }}
                        sx={{
                          backgroundColor: enableDarkTheme
                            ? alpha(_dark.boxBackground, 0.6)
                            : 'rgba(240, 245, 250, 0.8)',
                          p: 0.5,
                          border: `1px solid ${
                            enableDarkTheme
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(0, 0, 0, 0.05)'
                          }`,
                          color: enableDarkTheme
                            ? alpha(_dark.primary, 0.9)
                            : _light.primary,
                          '&:hover': {
                            backgroundColor: enableDarkTheme
                              ? alpha(_dark.boxBackground, 0.9)
                              : 'rgba(240, 245, 250, 1)',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        assets.length <= 2
                          ? 'Minimum 2 assets required'
                          : 'Remove Asset'
                      }
                    >
                      <IconButton
                        disabled={assets.length <= 2}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onRemoveAsset) {
                            // For Asset type objects, use assetId
                            const assetId =
                              'assetId' in item
                                ? item.assetId
                                : item.bumperNumber;
                            onRemoveAsset(assetId || '');
                          }
                        }}
                        sx={{
                          backgroundColor: enableDarkTheme
                            ? alpha(_dark.boxBackground, 0.6)
                            : 'rgba(240, 245, 250, 0.8)',
                          p: 0.5,
                          border: `1px solid ${
                            enableDarkTheme
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(0, 0, 0, 0.05)'
                          }`,
                          color: enableDarkTheme
                            ? 'rgba(239, 68, 68, 0.9)'
                            : '#ef4444',
                          '&:hover': {
                            backgroundColor: enableDarkTheme
                              ? alpha(_dark.boxBackground, 0.9)
                              : 'rgba(240, 245, 250, 1)',
                          },
                          '&:disabled': {
                            opacity: 0.5,
                            cursor: 'not-allowed',
                            color: enableDarkTheme
                              ? 'rgba(239, 68, 68, 0.5)'
                              : '#ef4444',
                            border: `1px solid ${
                              enableDarkTheme
                                ? 'rgba(255, 255, 255, 0.7)'
                                : 'rgba(0, 0, 0, 0.7)'
                            }`,
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '17px',
                    fontWeight: '600',
                    letterSpacing: '0.01em',
                    mb: 0.5,
                    color:
                      index === 0
                        ? '#E2C171'
                        : enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.95)'
                        : '#1e293b',
                  }}
                >
                  {item.bumperNumber}
                  {index === 0 ? (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        ml: 0.5,
                        color: enableDarkTheme
                          ? 'rgba(226, 193, 113, 0.8)'
                          : 'rgba(180, 150, 70, 0.9)',
                      }}
                    >
                      - {transformText(item.assetType)}
                    </Typography>
                  ) : null}
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {pngName ? (
                    <PNG
                      name={pngName}
                      style={{ height: '60px', width: '90px' }}
                    />
                  ) : (
                    <SVG
                      name={mapVehiclesAssets(item.assetType)}
                      style={{
                        fill: '#E2C171',
                        height: '30px',
                        width: '60px',
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}

        {assetDisplay.length < 4 ? (
          <Grid item lg={3} md={6} xs={12} key={'Unassigned-grid'}>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: enableDarkTheme
                  ? alpha(_dark.boxBackground, 0.4)
                  : 'rgba(248, 250, 252, 0.8)',
                borderRadius: '12px',
                minHeight: '98%',
                textAlign: 'center',
                color: enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.8)'
                  : 'rgba(30, 41, 59, 0.7)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: `1px dashed ${
                  enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.15)'
                    : 'rgba(0, 0, 0, 0.08)'
                }`,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: enableDarkTheme
                    ? alpha(_dark.boxBackground, 0.6)
                    : 'rgba(240, 245, 250, 0.9)',
                  borderColor: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(0, 0, 0, 0.15)',
                },
              }}
              onClick={() => {
                setShowModal(true);
              }}
            >
              <ControlPointIcon
                sx={{
                  fontSize: '28px',
                  color: enableDarkTheme
                    ? alpha(_dark.primary, 0.9)
                    : _light.primary,
                  mb: 1,
                }}
              />
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  letterSpacing: '0.01em',
                }}
              >
                {t('add_another_asset')}
              </Typography>
            </Paper>
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </Paper>
  );
};
